// HTTP client for the client portal — a simplified cut of
// apps/pwa/src/api/client.ts.
//
// Auth model (bearer, never cookies):
//   • Invite path:  POST /api/v1/invite/accept {code,email} → {sessionToken};
//     that signed session token IS the bearer for every authed call.
//   • Return path:  POST /auth/v1/email-otp/send-verification-otp then
//     POST /auth/v1/sign-in/email-otp → BA bearer token in the
//     `set-auth-token` response header (body.token fallback).
//   • Token persists in localStorage; any authed 401 clears it and routes
//     to /sign-in.
//
// `credentials: "omit"` everywhere: the server rejects requests carrying
// BOTH a cookie AND an Authorization header (AUTH_METHOD_CONFLICT), and the
// dev proxy origin can accumulate stale BA cookies.

import {
  APIError,
  type InvitePreview,
  type PortalClient,
  type PortalEventDetailResponse,
  type PortalEventRow,
  type PortalGuest,
  type PortalReportResponse,
} from "./types";

const APEX = (import.meta.env.VITE_ATTENDVIP_API_BASE ?? "").replace(/\/$/, "");
const BA = `${APEX}/auth/v1`;

const NO_COOKIES: RequestCredentials = "omit";
const SESSION_KEY = "attendvip:client-portal:session";

// ----- Session token storage ---------------------------------------------

export function getSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function setSessionToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(SESSION_KEY, token);
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* private browsing — don't crash sign-in */
  }
}

export function signOut(): void {
  const token = getSessionToken();
  setSessionToken(null);
  if (token) {
    // Best-effort server-side revocation; the portal doesn't wait on it.
    void fetch(`${BA}/sign-out`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      credentials: NO_COOKIES,
    }).catch(() => undefined);
  }
}

// 401 anywhere on an authed call → drop the token and land on /sign-in.
// Hard navigation (not router) keeps this module free of React imports.
function handleUnauthorized(): void {
  setSessionToken(null);
  if (!window.location.pathname.startsWith("/sign-in")) {
    window.location.assign("/sign-in");
  }
}

// ----- Error decoding ------------------------------------------------------

async function readErrorMessage(res: Response): Promise<string | null> {
  try {
    const data = await res.clone().json();
    if (typeof data?.error === "string" && data.error) return data.error;
    if (typeof data?.error?.message === "string" && data.error.message) return data.error.message;
    if (typeof data?.message === "string" && data.message) return data.message;
  } catch {
    /* not JSON */
  }
  try {
    const text = await res.clone().text();
    return text.split("\n")[0]?.slice(0, 200) ?? null;
  } catch {
    return null;
  }
}

async function check(res: Response, path: string): Promise<void> {
  if (res.ok) return;
  const msg = await readErrorMessage(res);
  switch (res.status) {
    case 400:
      throw new APIError("bad-request", msg ?? `Bad request — ${path}`, 400);
    case 401:
      throw new APIError("unauthorized", msg ?? "Unauthorized", 401);
    case 403:
      throw new APIError("forbidden", msg ?? `Access denied — ${path}`, 403);
    case 404:
      throw new APIError("not-found", msg ?? `Not found — ${path}`, 404);
    case 429:
      throw new APIError("rate-limited", "Too many attempts. Please wait a moment.", 429);
    default:
      throw new APIError(
        "server-error",
        msg ? `${res.status} ${msg}` : `Server error (${res.status})`,
        res.status,
      );
  }
}

// ----- Fetch helpers -------------------------------------------------------

async function publicPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${APEX}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: NO_COOKIES,
    body: JSON.stringify(body),
  });
  await check(res, path);
  return (await res.json()) as T;
}

async function authedFetch<T>(path: string, init: RequestInit): Promise<T> {
  const token = getSessionToken();
  if (!token) {
    handleUnauthorized();
    throw new APIError("unauthorized", "Not signed in", 401);
  }
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${APEX}${path}`, { ...init, headers, credentials: NO_COOKIES });
  if (res.status === 401) {
    handleUnauthorized();
    throw new APIError("unauthorized", "Session expired — please sign in again", 401);
  }
  await check(res, path);
  return (await res.json()) as T;
}

function authedGet<T>(path: string): Promise<T> {
  return authedFetch<T>(path, { method: "GET" });
}

function authedPost<T>(path: string, body: unknown): Promise<T> {
  return authedFetch<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ----- Public: invite + sign-in -------------------------------------------

export function previewInvite(code: string, email: string): Promise<InvitePreview> {
  return publicPost<InvitePreview>("/api/v1/client-portal/invite/preview", { code, email });
}

export async function acceptInvite(email: string, code: string): Promise<string> {
  const body = await publicPost<{ sessionToken?: string }>("/api/v1/invite/accept", {
    email,
    code,
  });
  if (!body.sessionToken) {
    throw new APIError("missing-auth-token", "Invite accepted but no token was returned");
  }
  return body.sessionToken;
}

export interface ResolveInviteBody {
  valid: boolean;
  needsEmail?: boolean;
  revoked?: boolean;
  expired?: boolean;
  bannedAccount?: boolean;
  bearerRefused?: boolean;
  purpose?: string | null;
  boundUserId?: string | null;
  boundEmailMasked?: string | null;
}

export function resolveInvite(code: string, email: string): Promise<ResolveInviteBody> {
  return publicPost<ResolveInviteBody>("/api/v1/invite/resolve", { code, email });
}

export async function attachInviteByCode(code: string): Promise<void> {
  await authedPost<unknown>("/api/v1/me/attach-event-by-code", { code });
}

export async function sendEmailOTP(email: string): Promise<void> {
  const res = await fetch(`${BA}/email-otp/send-verification-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: NO_COOKIES,
    body: JSON.stringify({ email, type: "sign-in" }),
  });
  await check(res, "/auth/v1/email-otp/send-verification-otp");
}

export async function signInWithEmailOTP(email: string, otp: string): Promise<string> {
  const res = await fetch(`${BA}/sign-in/email-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: NO_COOKIES,
    body: JSON.stringify({ email, otp }),
  });
  await check(res, "/auth/v1/sign-in/email-otp");
  // BA's bearer() plugin emits the session token in the `set-auth-token`
  // response header (case-insensitive lookup); body.token is the fallback.
  const headerToken = res.headers.get("set-auth-token");
  const body = (await res.json()) as { token?: string | null };
  const sessionToken = headerToken ?? body.token;
  if (!sessionToken) {
    throw new APIError("missing-auth-token", "Sign-in succeeded but no token was returned");
  }
  return sessionToken;
}

// ----- Authed: portal reads ------------------------------------------------

export async function listMyClients(): Promise<PortalClient[]> {
  const body = await authedGet<{ clients?: PortalClient[] }>("/api/v1/me/client-portal");
  return body.clients ?? [];
}

export async function listPortalEvents(): Promise<PortalEventRow[]> {
  const body = await authedGet<{ events?: PortalEventRow[] }>("/api/v1/me/client-portal/events");
  return body.events ?? [];
}

export function getPortalEvent(eventId: string): Promise<PortalEventDetailResponse> {
  return authedPost<PortalEventDetailResponse>("/api/v1/me/client-portal/event", { eventId });
}

export async function getPortalGuests(eventId: string): Promise<PortalGuest[]> {
  const body = await authedPost<{ guests?: PortalGuest[] }>(
    "/api/v1/me/client-portal/event/guests",
    { eventId },
  );
  return body.guests ?? [];
}

export function getPortalReport(eventId: string): Promise<PortalReportResponse> {
  return authedPost<PortalReportResponse>("/api/v1/me/client-portal/event/report", { eventId });
}
